import React, { useState, useRef, useEffect } from 'react';
import { Sparkles } from 'lucide-react';
import './SegmentOptimizerChat.css';
import useBillingQuota from '../components/Billing/useBillingQuota';
import { billingTranslations } from '../components/Billing/billingCopy';

const BASE_URL = 'https://dashboard-dot-primeval-gear-417120.ue.r.appspot.com';
const CUSTOMER_SAMPLE_LIMIT = 20;

/**
 * Segment Optimizer Chat — multi-turn chat against POST /segment-chat.
 *
 * Props:
 *  - isOpen: bool
 *  - onClose: fn
 *  - token: Bearer token (same auth as the rest of the dashboard API)
 *  - companyId: company id for the ?company= query param
 *  - initialContext: optional object from a Revenue Lever card
 *      ({ lever_name, target_segment, revenue_opportunity, ... }).
 *      Sent as initial_context on the FIRST request only.
 *  - onViewPlans: optional fn — navigate to the pricing page when the account
 *      is at its Active Revenue Opportunity limit.
 *  - onViewLeverPlay: optional fn — shown as a "View this play →" chip next
 *      to the lever context card when initialContext.lever_code is set. Lets
 *      the user jump straight to that play's card in Revenue Finder (e.g.
 *      from a Revenue Checkpoint metric that maps to a known lever).
 *  - allowActivate: optional bool, defaults to true. When false, the
 *      "Activate" button is never shown/enabled — used when this chat is
 *      opened from a Revenue Checkpoint metric, where activation in Klaviyo
 *      should only be possible from the lever card in Revenue Finder.
 *  - onGoToLever: optional fn(leverCode) — called when the user clicks
 *      "Go to this lever →" on a RecommendedLever card (shown when the
 *      backend response includes recommended_lever). Revenue Finder wires
 *      this to its existing deep-link highlight mechanism to scroll to and
 *      flash the matching card.
 *
 * Remount with a fresh `key` from the parent to start a new session.
 */
// Resalta montos y números (+$219, $1,234, 15%) en el texto del asistente.
function renderHighlighted(text) {
  if (!text) return text;
  const parts = String(text).split(/(\+?\$\s?[\d.,]+|\b\d[\d.,]*%?\b)/g);
  return parts.map((part, i) =>
    /^(\+?\$\s?[\d.,]+|\d[\d.,]*%?)$/.test(part)
      ? <strong key={i} className="soc-num">{part}</strong>
      : part
  );
}

export default function SegmentOptimizerChat({
  isOpen,
  onClose,
  token,
  companyId,
  initialContext,
  onViewPlans,
  onViewLeverPlay,
  allowActivate = true,
  onGoToLever,
  initialMessage,
}) {
  // Billing quota — fail-open (see useBillingQuota). When the account is at its
  // Active Revenue Opportunity limit, the activate button greys out and shows an
  // upgrade CTA instead of blocking silently. Downloading the CSV stays allowed.
  const { canActivate: billingCanActivate, refresh: refreshQuota } = useBillingQuota({ token });
  const capacityCopy = billingTranslations.en.capacity;
  const [messages, setMessages] = useState(() => {
    if (initialContext) {
      const parts = [];
      if (initialContext.lever_name) parts.push(`lever "${initialContext.lever_name}"`);
      if (initialContext.target_segment) parts.push(`target segment: ${initialContext.target_segment}`);
      if (initialContext.revenue_opportunity) parts.push(`revenue opportunity: ${initialContext.revenue_opportunity}`);
      return [{
        role: 'assistant',
        text: `Let's build the audience for the ${parts.join(' · ')}. ` +
          'Ask me to refine this segment — for example, filter by recency, ' +
          'order count or spend — and I\'ll return the matching customers.',
        local: true,
      }];
    }
    return [{
      role: 'assistant',
      text: 'Hi! I\'m your Segment Optimizer. Describe the audience you need ' +
        '(e.g. "VIP customers with no orders in the last 90 days") and I\'ll build it from your data.',
      local: true,
    }];
  });
  const [input, setInput] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [contextSent, setContextSent] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const bodyRef = useRef(null);
  const inputRef = useRef(null);

  // Activa en Klaviyo el último segmento de la sesión (o la palanca si hay contexto).
  // No crea campañas.
  const handleActivate = async () => {
    if (isActivating) return;
    const leverCode = initialContext && initialContext.lever_code;
    if (!leverCode && !sessionId) {
      setError('Build a segment first, then activate it.');
      return;
    }
    setIsActivating(true);
    setError(null);
    try {
      const url = leverCode
        ? `${BASE_URL}/levers/activate-klaviyo?company=${encodeURIComponent(companyId)}`
        : `${BASE_URL}/segment-chat/activate-klaviyo?company=${encodeURIComponent(companyId)}`;
      const payload = leverCode
        ? { lever: leverCode, source: 'segment_chat' }
        : { session_id: sessionId };
      const res = await fetch(
        url,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error((data && data.detail) || `Error ${res.status}`);
      setMessages((prev) => [...prev, {
        role: 'assistant',
        local: true,
        text: `Segment "${data.segment_name}" is now live in Klaviyo with ` +
          `${Number(data.profiles_sent || 0).toLocaleString()} profiles. ` +
          'Use it as the audience of your next campaign in Klaviyo.',
      }]);
      refreshQuota();
    } catch (err) {
      setError(err.message || 'Could not activate the segment.');
    } finally {
      setIsActivating(false);
    }
  };

  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  // sendMessage optionally accepts an override text (e.g. from a clicked
  // clarify option) instead of reading from the input field.
  const sendMessage = async (overrideText) => {
    const text = (typeof overrideText === 'string' ? overrideText : input).trim();
    if (!text || isLoading || !token || !companyId) return;

    setMessages((prev) => [...prev, { role: 'user', text }]);
    setInput('');
    setError(null);
    setIsLoading(true);

    const body = { message: text };
    if (sessionId) {
      body.session_id = sessionId;
    } else if (initialContext && !contextSent) {
      body.initial_context = initialContext;
    }

    try {
      const res = await fetch(
        `${BASE_URL}/segment-chat?company=${encodeURIComponent(companyId)}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        }
      );

      if (!res.ok) {
        let detail = `Error ${res.status}`;
        try {
          const errJson = await res.json();
          if (errJson && errJson.detail) detail = `${detail}: ${errJson.detail}`;
        } catch (e) { /* non-JSON error body */ }
        throw new Error(detail);
      }

      const data = await res.json();
      if (data.session_id) setSessionId(data.session_id);
      setContextSent(true);
      const clarifyOptions = Array.isArray(data.clarify_options) && data.clarify_options.length > 0
        ? data.clarify_options
        : null;
      // recommended_lever and clarify_options are mutually exclusive (backend contract).
      const recommendedLever = data.recommended_lever && typeof data.recommended_lever === 'object'
        && data.recommended_lever.lever
        ? data.recommended_lever
        : null;
      setMessages((prev) => [...prev, {
        role: 'assistant',
        text: data.reply_text || '',
        segment: data.segment || null,
        clarifyOptions,
        clarifyAnswered: false,
        recommendedLever,
      }]);
    } catch (err) {
      console.warn('Segment chat error:', err);
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Si se abrió con un mensaje precargado (ej. desde la caja "Segment Optimizer"
  // de Revenue Finder), lo envía automáticamente una sola vez al montar.
  const initialMessageSentRef = useRef(false);
  useEffect(() => {
    if (isOpen && initialMessage && initialMessage.trim() && !initialMessageSentRef.current) {
      initialMessageSentRef.current = true;
      sendMessage(initialMessage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialMessage]);

  const [isDownloading, setIsDownloading] = useState(false);
  const handleDownloadCsv = async () => {
    if (!sessionId || isDownloading) return;
    setIsDownloading(true);
    setError(null);
    try {
      const res = await fetch(
        `${BASE_URL}/segment-chat/export-csv?company=${encodeURIComponent(companyId)}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ session_id: sessionId }),
        }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data && data.detail) || `Error ${res.status}`);
      }
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = 'segment.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
    } catch (err) {
      setError(err.message || 'Could not download the CSV.');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Clicking a clarify option (from the backend) sends its exact text as the
  // next user message and locks that message's options so it can't be used twice.
  const handleClarifyOptionClick = (msgIdx, optionText) => {
    if (isLoading) return;
    setMessages((prev) => prev.map((m, i) => (
      i === msgIdx ? { ...m, clarifyAnswered: true } : m
    )));
    sendMessage(optionText);
  };

  // "Something else" is frontend-only — it never sends a message, it just
  // hands control back to the free-text input.
  const handleClarifyOther = (msgIdx) => {
    setMessages((prev) => prev.map((m, i) => (
      i === msgIdx ? { ...m, clarifyAnswered: true } : m
    )));
    if (inputRef.current) inputRef.current.focus();
  };

  return (
    <div className={`soc-drawer ${isOpen ? 'soc-drawer-active' : ''}`}>
      <div className="soc-header">
        <h5 className="soc-title">
          <Sparkles className="soc-title-icon soc-title-icon--ai" size={16} strokeWidth={2.25} aria-hidden="true" />
          Segment Optimizer
        </h5>
        <button className="soc-close-btn" onClick={onClose} aria-label="Close chat">
          <span className="material-symbols-outlined">close</span>
        </button>
      </div>

      {initialContext && (
        <div className="soc-context-card">
          <p className="soc-context-tag">Lever context</p>
          <p className="soc-context-line">
            {initialContext.lever_name}
            {initialContext.revenue_opportunity ? ` · ${initialContext.revenue_opportunity}` : ''}
          </p>
          {initialContext.target_segment && (
            <p className="soc-context-sub">{initialContext.target_segment}</p>
          )}
          {initialContext.lever_code && onViewLeverPlay && (
            <button
              type="button"
              className="soc-view-play-btn"
              onClick={onViewLeverPlay}
            >
              View this play in Revenue Plays →
            </button>
          )}
        </div>
      )}

      <div className="soc-body" ref={bodyRef}>
        {messages.map((msg, idx) => (
          <div key={idx} className={`soc-msg soc-msg-${msg.role}`}>
            <p className="soc-msg-text">{renderHighlighted(msg.text)}</p>
            {msg.clarifyOptions && (
              <ClarifyOptions
                options={msg.clarifyOptions}
                answered={Boolean(msg.clarifyAnswered)}
                onSelect={(optionText) => handleClarifyOptionClick(idx, optionText)}
                onOther={() => handleClarifyOther(idx)}
              />
            )}
            {msg.recommendedLever && (
              <RecommendedLever
                lever={msg.recommendedLever}
                onGoToLever={onGoToLever}
                onClose={onClose}
              />
            )}
            {msg.segment && (
              <SegmentResult
                segment={msg.segment}
                onActivate={handleActivate}
                activating={isActivating}
                canActivate={allowActivate && Boolean((initialContext && initialContext.lever_code) || sessionId)}
                billingLocked={!billingCanActivate}
                lockedCopy={capacityCopy}
                onViewPlans={onViewPlans}
                onDownload={handleDownloadCsv}
                downloading={isDownloading}
              />
            )}
          </div>
        ))}

        {isLoading && (
          <div className="soc-msg soc-msg-assistant">
            <p className="soc-msg-text soc-msg-loading">Building your segment…</p>
          </div>
        )}

        {error && (
          <div className="soc-error-box">
            <span className="material-symbols-outlined soc-error-icon">error</span>
            <span>{error}</span>
          </div>
        )}
      </div>

      <div className="soc-footer">
        <textarea
          ref={inputRef}
          className="soc-input"
          placeholder="Ask about your segment…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={2}
          disabled={isLoading}
        />
        <button
          className="soc-send-btn"
          onClick={() => sendMessage()}
          disabled={isLoading || !input.trim()}
        >
          {isLoading ? 'Asking…' : 'Ask →'}
        </button>
      </div>
    </div>
  );
}

// Renders the clarify question's options as clickable chips, plus a
// frontend-only "Something else" option that just focuses the free-text input.
function ClarifyOptions({ options, answered, onSelect, onOther }) {
  return (
    <div className="soc-clarify-options">
      {options.map((option, i) => (
        <button
          key={i}
          type="button"
          className="soc-clarify-option-btn"
          onClick={() => onSelect(option)}
          disabled={answered}
        >
          <span className="soc-clarify-option-num">{i + 1}</span>
          <span className="soc-clarify-option-text">{option}</span>
        </button>
      ))}
      <button
        type="button"
        className="soc-clarify-option-btn soc-clarify-option-other"
        onClick={onOther}
        disabled={answered}
      >
        <span className="soc-clarify-option-num">{options.length + 1}</span>
        <span className="soc-clarify-option-text">Something else (type it)</span>
      </button>
    </div>
  );
}

// Compact CTA shown when the backend detected that an existing Revenue Lever
// card already matches the user's request (recommended_lever). Points the user
// to that card instead of building a redundant segment from scratch.
function RecommendedLever({ lever, onGoToLever, onClose }) {
  const money = Number(lever.revenue_opportunity || 0).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
  const customers = Number(lever.customers || 0).toLocaleString('en-US');

  const handleClick = () => {
    // If the parent wired real navigation, hand it the stable lever id and
    // close the chat so the card is visible. Otherwise just close the chat.
    if (typeof onGoToLever === 'function') {
      onGoToLever(lever.lever);
    }
    if (typeof onClose === 'function') onClose();
  };

  return (
    <div className="soc-lever-card">
      <p className="soc-lever-tag">Recommended lever</p>
      <p className="soc-lever-title">{lever.lever_title || lever.lever}</p>
      {lever.audience_definition && (
        <p className="soc-lever-def">{lever.audience_definition}</p>
      )}
      <div className="soc-lever-stats">
        <span className="soc-lever-stat">
          <strong>{customers}</strong> customers
        </span>
        <span className="soc-lever-stat">
          <strong>{money}</strong> opportunity
        </span>
      </div>
      <button type="button" className="soc-lever-btn" onClick={handleClick}>
        Go to this lever →
      </button>
    </div>
  );
}

function SegmentResult({ segment, onActivate, activating, canActivate, billingLocked, lockedCopy, onViewPlans, onDownload, downloading }) {
  const customers = Array.isArray(segment.customers)
    ? segment.customers.slice(0, CUSTOMER_SAMPLE_LIMIT)
    : [];
  const showTruncatedNote = segment.truncated || segment.customers_truncated
    || (Array.isArray(segment.customers) && segment.customers.length > CUSTOMER_SAMPLE_LIMIT);

  return (
    <div className="soc-segment-card">
      <div className="soc-segment-header">
        <span className="soc-segment-count">
          {Number(segment.customer_count || 0).toLocaleString()} customers
        </span>
      </div>
      {segment.criteria_description && (
        <p className="soc-segment-criteria">{segment.criteria_description}</p>
      )}

      {customers.length > 0 && (
        <div className="soc-customer-table-wrap">
          <table className="soc-customer-table">
            <thead>
              <tr>
                <th>Customer ID</th>
                <th>Email</th>
              </tr>
            </thead>
            <tbody>
              {customers.map((c, i) => (
                <tr key={c.customer_id || i}>
                  <td>{c.customer_id}</td>
                  <td>{c.email}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showTruncatedNote && (
        <p className="soc-truncated-note">
          Showing a sample of {customers.length} — the full segment has{' '}
          {Number(segment.customer_count || 0).toLocaleString()} customers.
          Sync to Klaviyo or download the CSV to use them all.
        </p>
      )}

      <div className="soc-result-actions">
        {canActivate && billingLocked && (
          <>
            <button
              className="soc-activate-btn soc-activate-locked"
              disabled
              title={lockedCopy?.lockedTooltip}
            >
              {lockedCopy?.lockedTooltip || 'Plan limit reached — upgrade to activate'}
            </button>
            <button
              className="soc-activate-btn soc-upgrade-btn"
              onClick={() => onViewPlans && onViewPlans()}
            >
              {lockedCopy?.lockedCta || 'Upgrade'}
            </button>
          </>
        )}
        {canActivate && !billingLocked && (
          <button
            className="soc-activate-btn"
            onClick={onActivate}
            disabled={activating}
          >
            {activating ? 'ACTIVATING…' : 'REVIEW & ACTIVATE'}
          </button>
        )}
        <button
          className="soc-csv-btn"
          onClick={onDownload}
          disabled={downloading}
        >
          {downloading ? 'Preparing…' : 'Download CSV'}
        </button>
      </div>
    </div>
  );
}
